import { useEffect, useState, useCallback, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Radio, Users, Globe2, MapPin, Tv, BadgeCheck, Loader2 } from "lucide-react";

const RDC_GEO = `${process.env.PUBLIC_URL || ""}/drc-provinces.geojson`;
const WORLD_GEO = `${process.env.PUBLIC_URL || ""}/world-countries.geojson`;

const ISO_TO_PROVINCE = {
  "CD-BC": "Kongo-Central", "CD-BU": "Bas-Uele", "CD-EQ": "Equateur", "CD-HK": "Haut-Katanga",
  "CD-HL": "Haut-Lomami", "CD-HU": "Haut-Uele", "CD-IT": "Ituri", "CD-KC": "Kasai-Central",
  "CD-KE": "Kasai-Oriental", "CD-KG": "Kwango", "CD-KL": "Kwilu", "CD-KN": "Kinshasa",
  "CD-KS": "Kasai", "CD-LO": "Lomami", "CD-LU": "Lualaba", "CD-MA": "Maniema", "CD-MN": "Mai-Ndombe",
  "CD-MO": "Mongala", "CD-NK": "Nord-Kivu", "CD-NU": "Nord-Ubangi", "CD-SA": "Sankuru", "CD-SK": "Sud-Kivu",
  "CD-SU": "Sud-Ubangi", "CD-TA": "Tanganyika", "CD-TO": "Tshopo", "CD-TU": "Tshuapa",
};

function lerpColor(t) {
  const stops = [[156, 203, 232], [242, 169, 0], [200, 16, 46]];
  const seg = t <= 0.5 ? 0 : 1;
  const lt = t <= 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const a = stops[seg], b = stops[seg + 1];
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * lt));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function countryFlag(code) {
  if (!code || code.length !== 2) return "🌐";
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export default function LiveMap({ compact = false }) {
  const { t } = useTranslation();
  const [view, setView] = useState("world");
  const [worldGeo, setWorldGeo] = useState(null);
  const [rdcGeo, setRdcGeo] = useState(null);
  const [countries, setCountries] = useState({});
  const [provinces, setProvinces] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });
  const [countryModal, setCountryModal] = useState(null);
  const [countryLoading, setCountryLoading] = useState(false);

  const openCountry = useCallback((code, name) => {
    if (!code) return;
    setCountryLoading(true);
    setCountryModal({ country_code: code, country_name: name, channels: [] });
    api.get(`/live/country/${code}`).then((r) => setCountryModal(r.data)).catch(() => {}).finally(() => setCountryLoading(false));
  }, []);

  const load = useCallback(() => {
    api.get("/live/world").then((r) => {
      const map = {};
      (r.data.countries || []).forEach((c) => { map[c.code] = { viewers: c.viewers, name: c.name }; });
      setCountries(map);
    }).catch(() => {});
    api.get("/live/provinces").then((r) => {
      const map = {};
      (r.data.provinces || []).forEach((p) => { map[p.province] = p.viewers; });
      setProvinces(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(WORLD_GEO).then((r) => r.json()).then(setWorldGeo).catch(() => {});
    fetch(RDC_GEO).then((r) => r.json()).then(setRdcGeo).catch(() => {});
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 25000); return () => clearInterval(iv); }, [load]);

  const isWorld = view === "world";
  const W = isWorld ? 900 : 800;
  const H = isWorld ? 460 : 720;
  const geoData = isWorld ? worldGeo : rdcGeo;

  const projection = useMemo(() => {
    if (!geoData) return null;
    if (isWorld) return geoMercator().scale(compact ? 105 : 130).center([12, 8]).translate([W / 2, H / 2]);
    return geoMercator().fitExtent([[16, 16], [W - 16, H - 16]], geoData);
  }, [geoData, isWorld, W, H, compact]);

  // Recompute totals + list based on view
  useEffect(() => {
    if (isWorld) {
      const arr = Object.entries(countries).map(([code, v]) => ({ code, name: v.name, viewers: v.viewers }))
        .sort((a, b) => b.viewers - a.viewers);
      setTotal(arr.reduce((s, c) => s + c.viewers, 0));
      setList(arr.slice(0, 6).map((c) => ({ label: `${countryFlag(c.code)} ${c.name}`, viewers: c.viewers })));
    } else {
      const arr = Object.entries(provinces).map(([province, viewers]) => ({ province, viewers }))
        .sort((a, b) => b.viewers - a.viewers);
      setTotal(arr.reduce((s, p) => s + p.viewers, 0));
      setList(arr.slice(0, 6).map((p) => ({ label: p.province, viewers: p.viewers })));
    }
  }, [isWorld, countries, provinces]);

  const maxWorld = Math.max(1, ...Object.values(countries).map((c) => c.viewers));
  const maxRdc = Math.max(1, ...Object.values(provinces));

  return (
    <div className="rounded-3xl border border-border bg-card p-5 sm:p-6 relative overflow-hidden" data-testid="live-map">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rdcred opacity-70" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-rdcred" />
          </span>
          <h2 className="font-heading font-extrabold text-lg sm:text-xl tracking-tight">{t("livemap.title")}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-secondary p-1" data-testid="live-map-toggle">
            <button onClick={() => setView("world")} data-testid="live-toggle-world"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${isWorld ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <Globe2 size={13} /> {t("livemap.world")}
            </button>
            <button onClick={() => setView("rdc")} data-testid="live-toggle-rdc"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${!isWorld ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <MapPin size={13} /> {t("livemap.rdc")}
            </button>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-rdcred/10 text-rdcred px-3 py-1.5 font-bold" data-testid="live-total-viewers">
            <Users size={15} /> {total}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_240px] gap-6 items-center">
        <div className="relative">
          {projection && (
            <ComposableMap projection={projection} width={W} height={H} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={geoData}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    let name, v, testid;
                    if (isWorld) {
                      const code = geo.properties.ISO_A2;
                      const entry = countries[code];
                      v = entry ? entry.viewers : 0;
                      name = (entry && entry.name) || geo.properties.NAME;
                      testid = `map-country-${code || "xx"}`;
                    } else {
                      const iso = geo.properties.shapeISO;
                      name = ISO_TO_PROVINCE[iso] || geo.properties.shapeName;
                      v = provinces[name] || 0;
                      testid = `map-province-${iso}`;
                    }
                    const mx = isWorld ? maxWorld : maxRdc;
                    const fill = v > 0 ? lerpColor(v / mx) : "#eaeef3";
                    return (
                      <Geography key={geo.rsmKey} geography={geo} data-testid={testid}
                        fill={fill} stroke="#ffffff" strokeWidth={0.5}
                        onClick={() => { if (isWorld) openCountry(geo.properties.ISO_A2, name); }}
                        onMouseEnter={(e) => { setHover({ name, v }); setTip({ x: e.clientX, y: e.clientY }); }}
                        onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setHover(null)}
                        style={{
                          default: { fill, stroke: "#ffffff", strokeWidth: 0.5, outline: "none", transition: "fill 0.4s" },
                          hover: { fill: v > 0 ? fill : "#c3ccd6", stroke: "#0B3D91", strokeWidth: 1, outline: "none", cursor: "pointer" },
                          pressed: { fill, outline: "none" },
                        }} />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
          )}
          {hover && (
            <div className="fixed z-50 pointer-events-none rounded-lg bg-foreground text-background px-3 py-1.5 text-xs font-semibold shadow-lg"
              style={{ left: tip.x + 14, top: tip.y + 14 }}>
              {hover.name} · {hover.v} {t("livemap.viewers")}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
            {isWorld ? t("livemap.top_countries") : t("livemap.top_provinces")}
          </p>
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t("livemap.no_viewers")}</p>
          ) : (
            <ul className="space-y-2">
              {list.map((p, i) => (
                <li key={i} className="flex items-center gap-3" data-testid={`live-list-${i}`}>
                  <span className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: lerpColor(p.viewers / Math.max(1, list[0].viewers)), color: "#fff" }}>{i + 1}</span>
                  <span className="text-sm font-medium flex-1 truncate">{p.label}</span>
                  <span className="text-sm font-bold text-rdcred flex items-center gap-1"><Radio size={13} /> {p.viewers}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="inline-block w-16 h-2 rounded-full" style={{ background: "linear-gradient(90deg,#9CCBE8,#F2A900,#C8102E)" }} />
            {t("livemap.legend")}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">{t("livemap.realtime_note")}</p>
          {isWorld && <p className="text-[11px] text-primary mt-1 font-medium">{t("livemap.click_hint")}</p>}
        </div>
      </div>

      <Dialog open={!!countryModal} onOpenChange={(v) => { if (!v) setCountryModal(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="country-channels-modal">
          <DialogTitle className="flex items-center gap-2 text-lg font-heading">
            <Globe2 size={20} className="text-primary" /> {countryModal?.country_name}
          </DialogTitle>
          <p className="text-xs text-muted-foreground -mt-2">{t("livemap.channels_watched")}</p>
          {countryLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" size={26} /></div>
          ) : (countryModal?.channels || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="country-empty">{t("livemap.no_channels")}</p>
          ) : (
            <ul className="space-y-2">
              {countryModal.channels.map((c) => (
                <li key={c.id}>
                  <Link to={`/${c.type === "tv" ? "tv" : "radio"}/${c.slug}`} onClick={() => setCountryModal(null)}
                    data-testid={`country-channel-${c.slug}`}
                    className="flex items-center gap-3 rounded-xl border border-border p-2.5 hover:border-primary/50 transition-colors">
                    <img src={c.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=0B3D91&color=fff`} alt="" className="w-10 h-10 rounded-lg bg-white shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate flex items-center gap-1">{c.name} {c.certified && <BadgeCheck size={13} className="text-rdcgold shrink-0" />}</p>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">{c.type === "tv" ? <Tv size={11} /> : <Radio size={11} />} {c.province}</p>
                    </div>
                    <span className="text-sm font-bold text-rdcred flex items-center gap-1 shrink-0"><Users size={13} /> {c.viewers}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
